// Registry of dashboard section types. Each type knows which backend
// endpoint supplies its items and a descriptive note, so the dashboard can
// be driven by config (and by a user's saved layout) instead of hardcoded
// markup. Keep new types additive and backed by an existing endpoint.

export const SECTION_TYPES = {
  trending: { label: 'Trending', endpoint: '/movies/trending', note: 'Trending on VideBits — popularity + on-platform momentum' },
  'in-cinemas': { label: 'In Cinemas', endpoint: '/movies/now-playing', note: 'In theaters now (TMDB)' },
  'top-rated': { label: 'Top Rated', endpoint: '/movies/top-rated', note: 'Ratings from TMDB (≥ 8.0, 10k+ votes)' },
  'hidden-gems': { label: 'Hidden Gems', endpoint: '/movies/hidden-gems', note: 'Ratings from TMDB (≥ 7.5, under 10k votes)' },
  'cult-classics': { label: 'Cult Classics', endpoint: '/movies/cult-classics', note: 'Beloved cult films with devoted followings' },
  popular: { label: 'Popular', endpoint: '/movies/popular', note: 'Popular on TMDB right now' },
  // Authed type: recommendations from the signed-in user's watchlist.
  'for-you': { label: 'For You', endpoint: '/movies/for-you', authed: true, note: 'Recommended from your watchlist' },
  // Dynamic types: no fixed endpoint — the URL is built from params.
  genre: { label: 'Genre', dynamic: true },
  discover: { label: 'Discover', dynamic: true },
};

// Genres offered by the editor (names match what the discover endpoint maps
// to TMDB genres).
export const GENRES = [
  'Action', 'Adventure', 'Animation', 'Comedy', 'Crime', 'Documentary',
  'Drama', 'Family', 'Fantasy', 'Horror', 'Mystery', 'Romance',
  'Science Fiction', 'Thriller', 'War', 'Western',
];

// Filter options offered for "Discover" sections.
export const DECADES = [2020, 2010, 2000, 1990, 1980, 1970];
export const SORT_OPTIONS = [
  { value: 'popularity.desc', label: 'Most popular' },
  { value: 'vote_average.desc', label: 'Highest rated' },
  { value: 'release_date.desc', label: 'Newest' },
];

// Default home layout for users without a saved one. Matches the previous
// hardcoded landing carousels (the note is derived from the type registry).
export const DEFAULT_SECTIONS = [
  { id: 'trending', type: 'trending', title: 'Trending Now' },
  { id: 'in-cinemas', type: 'in-cinemas', title: 'In Cinemas' },
  { id: 'top-rated', type: 'top-rated', title: 'Top Rated' },
  { id: 'hidden-gems', type: 'hidden-gems', title: 'Hidden Gems' },
  { id: 'cult-classics', type: 'cult-classics', title: 'Cult Classics' },
];

// Build the fetch URL for a section, or null if it cannot be fetched yet
// (unknown type, or a dynamic type missing its params).
export function sectionEndpoint(section, { limit = 20, page = 1 } = {}) {
  const def = SECTION_TYPES[section.type];
  if (!def) return null;
  if (def.endpoint) return `${def.endpoint}?limit=${limit}&page=${page}`;
  if (section.type === 'genre') {
    const g = section.params && section.params.genre;
    if (!g) return null;
    const media = (section.params && section.params.media) || 'movie';
    return `/search/advanced?genres=${encodeURIComponent(g)}&media_type=${media}&limit=${limit}&page=${page}`;
  }
  if (section.type === 'discover') {
    const p = section.params || {};
    const qs = new URLSearchParams({ media_type: p.media || 'movie', limit: String(limit), page: String(page) });
    if (p.decade) {
      qs.set('year_from', String(p.decade));
      qs.set('year_to', String(Number(p.decade) + 9));
    }
    if (p.ratingFrom != null && p.ratingFrom !== '') qs.set('rating_from', String(p.ratingFrom));
    qs.set('sort_by', p.sortBy || 'popularity.desc');
    return `/search/advanced?${qs.toString()}`;
  }
  return null;
}

// A /browse link for a section — carries a minimal descriptor so the browse
// page can rebuild the endpoint and paginate. Null for un-fetchable sections
// (unknown type, or a dynamic type missing its params).
export function browseHref(section) {
  if (!sectionEndpoint(section)) return null;
  const payload = { type: section.type, title: section.title, params: section.params || {} };
  return `/browse?s=${encodeURIComponent(JSON.stringify(payload))}`;
}

// Descriptive note for a section. Derived (not stored), so it survives
// save/load and reflects the current params.
export function sectionNote(section) {
  const def = SECTION_TYPES[section.type];
  if (!def) return undefined;
  if (section.type === 'genre') {
    const g = (section.params && section.params.genre) || 'a genre';
    const media = (section.params && section.params.media) === 'tv' ? 'shows' : 'movies';
    return `${g} ${media} from TMDB`;
  }
  if (section.type === 'discover') {
    const p = section.params || {};
    const media = p.media === 'tv' ? 'shows' : 'movies';
    const bits = [];
    if (p.decade) bits.push(`${p.decade}s`);
    if (p.ratingFrom) bits.push(`★ ${p.ratingFrom}+`);
    return `${media}${bits.length ? ' · ' + bits.join(' · ') : ''} from TMDB`;
  }
  return def.note;
}

// Section types a user can add from the editor.
export const ADDABLE_TYPES = Object.entries(SECTION_TYPES).map(
  ([type, def]) => ({ type, label: def.label })
);

// Create a fresh section of the given type with a unique id.
export function newSection(type) {
  const def = SECTION_TYPES[type];
  const id = `${type}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  if (type === 'genre') {
    const genre = GENRES[0];
    return { id, type, title: genre, visible: true, params: { genre, media: 'movie' } };
  }
  if (type === 'discover') {
    return { id, type, title: 'Discover', visible: true, params: { decade: 2010, ratingFrom: 7, sortBy: 'popularity.desc', media: 'movie' } };
  }
  return { id, type, title: def ? def.label : type, visible: true, params: {} };
}
